// src/pages/WorkflowsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db } from '../firebase';
import { getStorage, ref, getBlob } from 'firebase/storage';
import { collection, getDocs, orderBy, query as query_fs, where, doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { Tag, SectionHeader, LoadingRow, BackButton } from '../components/Nav';

function storagePathFromUrl(url) {
  try {
    const match = url.match(/\/o\/(.+?)(\?|$)/);
    if (match) return decodeURIComponent(match[1]);
  } catch {}
  return null;
}

async function downloadFile(url, filename) {
  try {
    const storage = getStorage();
    const path = storagePathFromUrl(url);
    if (!path) throw new Error('Could not parse storage path');
    const fileRef = ref(storage, path);
    const blob = await getBlob(fileRef);
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl; a.download = filename || 'workflow.json';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  } catch (e) {
    console.error('[download] error:', e);
    window.open(url, '_blank');
  }
}

// Fire-and-forget: bumps the workflow's downloadCount by exactly 1.
// Not awaited by callers — we don't want a slow/blocked write to delay
// the actual file download the visitor asked for. Firestore rules only
// allow this exact +1 shape, so this can never be abused to set an
// arbitrary count.
async function bumpDownloadCount(workflowId) {
  if (!workflowId) return;
  try {
    await updateDoc(doc(db, 'workflows', workflowId), { downloadCount: increment(1) });
  } catch (e) {
    console.error('[downloadCount] failed to increment:', e);
  }
}

// Formats a download count: plain integer under 1000, "1.2k"-style above
// it. No trailing arrow character here anymore -- the arrow read as a
// downvote symbol next to a bare number, so it's now a proper icon
// rendered alongside this via <DownloadIcon />, not baked into the string.
function formatDownloadCount(n) {
  if (!n) return null;
  if (n < 1000) return `${n}`;
  return `${(n / 1000).toFixed(1)}k`;
}

// A single source-of-truth download icon (arrow dropping into an open
// tray). Rendered at different sizes via width/height only -- the
// viewBox and stroke-width stay fixed, so it scales cleanly instead of
// getting redrawn/re-guessed per size.
function DownloadIcon({ size = 14, color = 'var(--sand-hint)' }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="3" x2="12" y2="15" />
      <polyline points="7 10 12 15 17 10" />
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    </svg>
  );
}

// Picks the gallery thumbnail for a workflow: the admin-selected cover
// image if it's still a valid still image, otherwise the first image in
// the workflow's own gallery (videos excluded — they can't be a static
// thumbnail). Returns null if the workflow has no usable images at all.
function coverImageFor(workflow) {
  const isImage = src => {
    const p = src.split('?')[0].toLowerCase();
    return !/\.(mp4|webm|mov|avi)/.test(p);
  };
  if (workflow.coverImage && isImage(workflow.coverImage)) return workflow.coverImage;
  return (workflow.images || []).find(isImage) || null;
}

function Lightbox({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const prev = useCallback(() => setIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const saved = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = saved; };
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape')     onClose();
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, prev, next]);

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(20,14,6,0.93)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
      <button onClick={e => { e.stopPropagation(); onClose(); }} style={{ position: 'fixed', top: 18, right: 22, width: 38, height: 38, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, color: '#fff', fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10000 }}>×</button>
      {images.length > 1 && <button onClick={e => { e.stopPropagation(); prev(); }} style={{ position: 'fixed', left: 18, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, color: '#fff', fontSize: 24, width: 44, height: 44, cursor: 'pointer', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>}
      <img src={images[idx]} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: '88vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: 6, boxShadow: '0 12px 48px rgba(0,0,0,0.7)', cursor: 'default' }} />
      {images.length > 1 && <button onClick={e => { e.stopPropagation(); next(); }} style={{ position: 'fixed', right: 18, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, color: '#fff', fontSize: 24, width: 44, height: 44, cursor: 'pointer', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>}
      {images.length > 1 && <div style={{ position: 'fixed', bottom: 18, left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 10000 }}><span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{idx + 1} / {images.length}</span></div>}
    </div>,
    document.body
  );
}

function WorkflowRow({ workflow }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={`/workflows/${workflow.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', background: hovered ? 'var(--sand-surface)' : 'transparent', borderBottom: '0.5px solid var(--sand-border)', cursor: 'pointer', transition: 'background 0.15s', borderRadius: hovered ? 'var(--radius-md)' : 0, textDecoration: 'none', color: 'inherit' }}
    >
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--sand-hint)', minWidth: 24, flexShrink: 0 }}>{String(workflow.order).padStart(2, '0')}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: "'Source Serif 4', serif", fontSize: 14, fontWeight: 500, color: 'var(--sand-text)' }}>{workflow.name}</span>
          <div style={{ display: 'flex', gap: 4 }}>{workflow.tags?.map(t => <Tag key={t} label={t} />)}</div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--sand-muted)', marginTop: 3, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{workflow.shortDescription}</p>
      </div>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, minWidth: 60, justifyContent: 'flex-end' }}>
        {formatDownloadCount(workflow.downloadCount) ? (
          <>
            <DownloadIcon size={12} color="var(--sand-hint)" />
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)' }}>{formatDownloadCount(workflow.downloadCount)}</span>
          </>
        ) : (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)' }}>—</span>
        )}
      </span>
      <span style={{ color: 'var(--sand-amber)', fontSize: 14, flexShrink: 0 }}>→</span>
    </Link>
  );
}

function WorkflowCard({ workflow }) {
  const [hovered, setHovered] = useState(false);
  const cover = coverImageFor(workflow);
  return (
    <Link
      to={`/workflows/${workflow.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', flexDirection: 'column', cursor: 'pointer',
        background: 'var(--sand-bg)', border: `0.5px solid ${hovered ? 'var(--sand-amber-border)' : 'var(--sand-border)'}`,
        borderRadius: 'var(--radius-lg)', overflow: 'hidden',
        boxShadow: hovered ? '0 6px 20px rgba(0,0,0,0.18)' : 'none',
        transform: hovered ? 'translateY(-2px)' : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        textDecoration: 'none', color: 'inherit',
      }}
    >
      <div style={{ position: 'relative', aspectRatio: '16 / 10', background: '#0b0f16', overflow: 'hidden' }}>
        {cover ? (
          <img src={cover} alt={workflow.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: hovered ? 'scale(1.04)' : 'scale(1)', transition: 'transform 0.25s' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)' }}>No preview</span>
          </div>
        )}
        {formatDownloadCount(workflow.downloadCount) && (
          <span style={{ position: 'absolute', top: 8, right: 8, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, padding: '3px 8px', borderRadius: 10, background: 'rgba(11,15,22,0.9)', color: 'var(--sand-amber)' }}>
            <DownloadIcon size={12} color="var(--sand-amber)" />
            {formatDownloadCount(workflow.downloadCount)}
          </span>
        )}
      </div>
      <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{ fontFamily: "'Source Serif 4', serif", fontSize: 14, fontWeight: 500, color: 'var(--sand-text)' }}>{workflow.name}</span>
        </div>
        <p style={{ fontSize: 12, color: 'var(--sand-muted)', lineHeight: 1.4, marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {workflow.shortDescription}
        </p>
        {workflow.tags?.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 'auto' }}>
            {workflow.tags.map(t => <Tag key={t} label={t} />)}
          </div>
        )}
      </div>
    </Link>
  );
}

function WorkflowDetail({ workflow, onBack, onDownload }) {
  const [lightbox, setLightbox] = useState(null);

  const imageUrls = (workflow.images || []).filter(src => {
    const p = src.split('?')[0].toLowerCase();
    return !/\.(mp4|webm|mov|avi)/.test(p);
  });

  // Normalise: support both old downloadUrl and new downloads array
  const downloads = (() => {
    if (workflow.downloads?.length > 0) return workflow.downloads;
    if (workflow.downloadUrl) return [{ id: '0', label: 'Download workflow', url: workflow.downloadUrl }];
    return [];
  })();

  function filenameFromUrl(url) {
    try {
      const encodedPath = url.match(/\/o\/(.+?)(\?|$)/)?.[1] || '';
      const decoded = decodeURIComponent(encodedPath);
      return decoded.split('/').pop().replace(/^\d+_/, '') || 'workflow.json';
    } catch { return 'workflow.json'; }
  }

  // Detect if longDescription is HTML or plain text
  const isHTML = workflow.longDescription?.trim().startsWith('<');

  return (
    <div className="fade-up">
      <BackButton onClick={onBack} />

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, color: 'var(--sand-text)', letterSpacing: '-0.5px' }}>{workflow.name}</h1>
          <div style={{ display: 'flex', gap: 4 }}>{workflow.tags?.map(t => <Tag key={t} label={t} />)}</div>
        </div>
        <p style={{ fontSize: 14, color: 'var(--sand-muted)', lineHeight: 1.6 }}>{workflow.shortDescription}</p>
      </div>

      {/* Download CTA — single or multiple variants */}
      <div style={{ marginBottom: 32, padding: '16px 18px', background: 'var(--sand-surface)', border: '1px solid var(--sand-amber-border)', borderRadius: 'var(--radius-md)' }}>
        {downloads.length === 0 ? (
          <span style={{ fontSize: 12, color: 'var(--sand-hint)', fontStyle: 'italic' }}>Download link coming soon.</span>
        ) : downloads.length === 1 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ flex: 1, fontSize: 12, color: 'var(--sand-muted)' }}>Ready to use · JSON · ComfyUI 0.2+</span>
            <button
              onClick={() => { downloadFile(downloads[0].url, filenameFromUrl(downloads[0].url)); onDownload(); }}
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, padding: '8px 18px', borderRadius: 'var(--radius-sm)', background: 'var(--sand-amber)', color: '#0e1525', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >{downloads[0].label || 'Download workflow'} ↓</button>
          </div>
        ) : (
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
              {downloads.length} versions — pick the one that fits your setup
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {downloads.map((d, i) => (
                <button key={d.id || i}
                  onClick={() => { downloadFile(d.url, filenameFromUrl(d.url)); onDownload(); }}
                  style={{
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    padding: '9px 18px', borderRadius: 'var(--radius-sm)',
                    background: i === 0 ? 'var(--sand-amber)' : 'transparent',
                    color: i === 0 ? '#0e1525' : 'var(--sand-amber)',
                    border: i === 0 ? 'none' : '1px solid var(--sand-amber-border)',
                    cursor: 'pointer', whiteSpace: 'nowrap', transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >{d.label || `Version ${i + 1}`} ↓</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Preview — BEFORE About ── */}
      {workflow.images && workflow.images.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: 'var(--sand-text)', marginBottom: 12 }}>Preview</h3>
          <style>{`
            .wf-preview-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
              gap: 12px;
            }
            .wf-preview-item {
              position: relative;
              height: 220px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: var(--radius-md);
              overflow: hidden;
              border: 0.5px solid var(--sand-border);
              background: #0b0f16;
            }
            .wf-preview-item img,
            .wf-preview-item video {
              width: 100%;
              height: 100%;
              object-fit: contain;
              display: block;
            }
          `}</style>
          <div className="wf-preview-grid">
            {workflow.images.map((src, i) => {
              const pathPart = src.split('?')[0].toLowerCase();
              const isVideo = /\.(mp4|webm|mov|avi)/.test(pathPart);
              const imageIdx = imageUrls.indexOf(src);
              return (
                <div key={i} className="wf-preview-item" style={{ cursor: isVideo ? 'default' : 'zoom-in' }}>
                  {isVideo ? (
                    <video src={src} controls preload="metadata" />
                  ) : (
                    <img src={src} alt={`${workflow.name} preview ${i + 1}`} onClick={() => setLightbox(imageIdx)} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── About this workflow — AFTER Preview ── */}
      {workflow.longDescription && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: 'var(--sand-text)', marginBottom: 14 }}>About this workflow</h3>
          <div style={{ fontSize: 13, color: 'var(--sand-muted)', lineHeight: 1.9, padding: '18px 20px', background: 'var(--sand-surface)', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--sand-border)' }}>
            {isHTML ? (
              <>
                <style>{`
                  .wf-rich h2 { font-size:17px; font-weight:600; margin:10px 0 6px; font-family:'Playfair Display',serif; color:var(--sand-text); }
                  .wf-rich h3 { font-size:14px; font-weight:600; margin:8px 0 4px; font-family:'Playfair Display',serif; color:var(--sand-text); }
                  .wf-rich h4 { font-size:12px; font-weight:600; margin:6px 0 3px; font-family:'Playfair Display',serif; color:var(--sand-text); text-transform:uppercase; letter-spacing:0.5px; }
                  .wf-rich img { max-width:100%; border-radius:6px; margin:8px 0; display:block; }
                  .wf-rich p  { margin:0 0 12px; min-height:1.4em; }
                  .wf-rich a  { color:#38bdf8; text-decoration:underline; }
                  .wf-rich strong { font-weight:600; color:var(--sand-text); }
                  .wf-rich em { font-style:italic; }
                  .wf-rich iframe { width:100%; aspect-ratio:16/9; height:auto; border-radius:6px; margin:8px 0; display:block; }
                `}</style>
                <div className="wf-rich" dangerouslySetInnerHTML={{ __html: workflow.longDescription }} />
              </>
            ) : (
              <span style={{ whiteSpace: 'pre-line' }}>{workflow.longDescription}</span>
            )}
          </div>
        </div>
      )}

      {/* Meta footer */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)', paddingTop: 16, borderTop: '0.5px solid var(--sand-border)' }}>
        <span>{workflow.nodeCount} nodes</span>
        {workflow.downloadCount && <span>{workflow.downloadCount.toLocaleString()} downloads</span>}
        {workflow.tags?.map(t => <span key={t}>#{t}</span>)}
      </div>

      {lightbox !== null && <Lightbox images={imageUrls} startIndex={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

export default function WorkflowsTab() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workflows, setWorkflows]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [detail, setDetail]         = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── Search / filter state ──────────────────────────────────────────────────
  const [query, setQuery] = useState('');

  // ── Gallery vs list view — defaults to gallery, remembers the visitor's
  // last choice across visits. ────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('wf_view_mode') === 'list' ? 'list' : 'gallery'; }
    catch { return 'gallery'; }
  });
  function setView(mode) {
    setViewMode(mode);
    try { localStorage.setItem('wf_view_mode', mode); } catch {}
  }

  useEffect(() => {
    if (!detail?.name) return;
    const originalTitle = document.title;
    document.title = `ComfyStack · ${detail.name}`;
    return () => { document.title = originalTitle; };
  }, [detail]);

  useEffect(() => {
    async function load() {
      try {
        const q = query_fs(collection(db, 'workflows'), where('published', '==', true), orderBy('order', 'asc'));
        const snap = await getDocs(q);
        setWorkflows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error('Failed to load workflows:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (!id) { setDetail(null); return; }
    async function loadDetail() {
      setDetailLoading(true);
      try {
        const fromList = workflows.find(w => w.id === id);
        if (fromList) { setDetail(fromList); return; }
        const snap = await getDoc(doc(db, 'workflows', id));
        if (snap.exists()) setDetail({ id: snap.id, ...snap.data() });
        else navigate('/workflows', { replace: true });
      } catch (e) {
        navigate('/workflows', { replace: true });
      } finally {
        setDetailLoading(false);
      }
    }
    loadDetail();
  }, [id, workflows, navigate]);

  // Called when a visitor clicks a download button on the detail page.
  // Writes the real increment to Firestore (fire-and-forget) and bumps
  // the locally-held counts so the UI reflects it immediately, both on
  // the open detail view and in the cached list (so going back shows it
  // updated too, without waiting on a refetch).
  function handleDownload() {
    if (!detail) return;
    bumpDownloadCount(detail.id);
    setDetail(prev => prev ? { ...prev, downloadCount: (prev.downloadCount || 0) + 1 } : prev);
    setWorkflows(prev => prev.map(w => w.id === detail.id ? { ...w, downloadCount: (w.downloadCount || 0) + 1 } : w));
  }

  if (id) {
    if (detailLoading || !detail) return <LoadingRow />;
    return <WorkflowDetail workflow={detail} onBack={() => navigate('/workflows')} onDownload={handleDownload} />;
  }

  // ── Filtering ──────────────────────────────────────────────────────────────
  const trimmed = query.trim().toLowerCase();
  const textActive = trimmed.length >= 3;

  const filtered = workflows.filter(wf => {
    if (!textActive) return true;
    return (
      wf.name?.toLowerCase().includes(trimmed) ||
      wf.shortDescription?.toLowerCase().includes(trimmed) ||
      wf.tags?.some(t => t.toLowerCase().includes(trimmed))
    );
  });

  const hasFilters = textActive;

  function clearAll() { setQuery(''); }

  return (
    <div className="fade-up">
      {/* ── Search bar + gallery/list toggle ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--sand-hint)" strokeWidth="2"
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={loading ? 'Search workflows…' : `Search ${workflows.length} workflows… (3+ letters)`}
            style={{
              width: '100%', padding: '9px 32px 9px 32px',
              background: 'var(--sand-surface)', color: 'var(--sand-text)',
              border: `0.5px solid ${textActive ? 'var(--sand-amber)' : 'var(--sand-border)'}`,
              borderRadius: 'var(--radius-sm)', fontSize: 13,
              fontFamily: "'Source Serif 4', serif", outline: 'none',
              transition: 'border-color 0.15s',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--sand-amber)'}
            onBlur={e => e.target.style.borderColor = textActive ? 'var(--sand-amber)' : 'var(--sand-border)'}
          />
          {query ? (
            <button onClick={() => setQuery('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sand-hint)', fontSize: 16, lineHeight: 1, padding: '2px 4px' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--sand-text)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--sand-hint)'}
            >×</button>
          ) : null}
        </div>

        <div style={{ display: 'flex', border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', flexShrink: 0 }}>
          <button
            onClick={() => setView('gallery')}
            title="Gallery view"
            style={{
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: viewMode === 'gallery' ? 'var(--sand-amber-light)' : 'var(--sand-surface)',
              border: 'none', borderRight: '0.5px solid var(--sand-border)', cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={viewMode === 'gallery' ? 'var(--sand-amber)' : 'var(--sand-hint)'} strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </button>
          <button
            onClick={() => setView('list')}
            title="List view"
            style={{
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: viewMode === 'list' ? 'var(--sand-amber-light)' : 'var(--sand-surface)',
              border: 'none', cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={viewMode === 'list' ? 'var(--sand-amber)' : 'var(--sand-hint)'} strokeWidth="2">
              <line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Active filter badge — only shown when filtering */}
      {hasFilters && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)' }}>
            {filtered.length} of {workflows.length} shown
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, padding: '2px 8px', borderRadius: 10, background: 'var(--sand-amber-light)', color: 'var(--sand-amber)', border: '0.5px solid var(--sand-amber-border)' }}>
            "{trimmed}"
            <span onClick={() => setQuery('')} style={{ cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>×</span>
          </span>
        </div>
      )}

      {loading ? <LoadingRow /> : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>🔍</div>
          <p style={{ fontSize: 14, color: 'var(--sand-muted)', marginBottom: 8 }}>No workflows match your search.</p>
          <button onClick={clearAll} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--sand-amber)', background: 'none', border: '0.5px solid var(--sand-amber-border)', borderRadius: 'var(--radius-sm)', padding: '6px 16px', cursor: 'pointer' }}>Clear filters</button>
        </div>
      ) : viewMode === 'gallery' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {filtered.map(wf => <WorkflowCard key={wf.id} workflow={wf} />)}
        </div>
      ) : (
        <div style={{ border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--sand-bg)' }}>
          <div style={{ display: 'flex', gap: 16, padding: '8px 20px', background: 'var(--sand-surface)', borderBottom: '0.5px solid var(--sand-border)' }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)', minWidth: 24 }}>#</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)', flex: 1 }}>Workflow</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)', minWidth: 60, textAlign: 'right' }}>Downloads</span>
            <span style={{ width: 14 }} />
          </div>
          {filtered.map(wf => <WorkflowRow key={wf.id} workflow={wf} />)}
        </div>
      )}
    </div>
  );
}
